import { lazy, Suspense, Component, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Lenis from "lenis";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Dates from "@/pages/public/Dates";
import EventDetail from "@/pages/public/EventDetail";
import Music from "@/pages/public/Music";
import ReleaseDetail from "@/pages/public/ReleaseDetail";
import Media from "@/pages/public/Media";
import BookingPage from "@/pages/public/BookingPage";
import Legal from "@/pages/public/Legal";
import NotFound from "@/pages/public/NotFound";

const AdminApp = lazy(() => import("@/pages/admin/AdminApp"));

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 48, background: "#050505", color: "#fff", minHeight: "100vh" }}>
          <p className="font-display text-4xl">SOMETHING WENT WRONG.</p>
          <p className="mt-3 text-sm text-white/60">{String(this.state.err?.message || this.state.err)}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  const { pathname } = useLocation();
  const onPublicSite = !pathname.startsWith("/admin");
  useEffect(() => {
    if (!onPublicSite) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [onPublicSite]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-ink text-white">
      <Nav />
      <main>{children}</main>
      <Footer />
      <div className="grain-overlay" aria-hidden="true" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <SmoothScroll />
        <Routes>
          <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
          <Route path="/dates" element={<PublicLayout><Dates /></PublicLayout>} />
          <Route path="/dates/:slug" element={<PublicLayout><EventDetail /></PublicLayout>} />
          <Route path="/music" element={<PublicLayout><Music /></PublicLayout>} />
          <Route path="/music/:slug" element={<PublicLayout><ReleaseDetail /></PublicLayout>} />
          <Route path="/media" element={<PublicLayout><Media /></PublicLayout>} />
          <Route path="/booking" element={<PublicLayout><BookingPage /></PublicLayout>} />
          <Route path="/impressum" element={<PublicLayout><Legal page="impressum" /></PublicLayout>} />
          <Route path="/datenschutz" element={<PublicLayout><Legal page="datenschutz" /></PublicLayout>} />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={null}>
                <AdminApp />
              </Suspense>
            }
          />
          <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
        </Routes>
        <Toaster theme="dark" position="top-center" toastOptions={{ style: { borderRadius: 0, background: "#111", border: "1px solid #222", color: "#fff" } }} />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
